import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { ThemeContext } from "../../../../../context/ThemeContext";
import { postData } from "../../../../../services/Fetchnodeservices";

// ===================== ICONS =====================
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const TrendingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const WalletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 6v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
    <path d="M22 10h-4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h4" />
    <circle cx="18" cy="14" r="1" fill="currentColor" />
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ===================== STAT CARD =====================
const StatCard = ({ icon, title, value, color, theme }) => {
  return (
    <div
      style={{
        background: theme.surface,
        padding: 20,
        borderRadius: 16,
        boxShadow: `0 4px 12px ${theme.primary}10`,
        border: `1px solid ${theme.border}`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}20`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}10`;
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: `${color}1a`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
          }}
        >
          {icon}
        </div>
        <h4 style={{ margin: 0, fontSize: 13, color: theme.textSecondary, fontWeight: 500 }}>
          {title}
        </h4>
      </div>
      <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: theme.textPrimary }}>
        {value}
      </h2>
    </div>
  );
};

// ===================== MAIN COMPONENT =====================
export default function VerifiedCashDeposits() {
  const { theme } = useContext(ThemeContext); // ← Theme Context

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    const result = await postData(
      "vandersuperadmin/admin/cash/verified-deposits",
      {}
    );
    console.log("VERIFIED:", result);
    if (result.success) {
      setRows(result.data);
    } else {
      setRows([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredRows = useMemo(() => {
    return rows.filter((item) => {
      const keyword = search.toLowerCase();
      const restaurants = (item.orders || [])
        .map((o) => o.restaurant_name)
        .join(" ")
        .toLowerCase();
      const orderIds = (item.orders || [])
        .map((o) => o.id)
        .join(" ");

      const matchSearch =
        item.driver_name?.toLowerCase().includes(keyword) ||
        restaurants.includes(keyword) ||
        orderIds.includes(keyword);

      const rowDate = item.verified_at?.split("T")[0];
      const matchFrom = !fromDate || rowDate >= fromDate;
      const matchTo = !toDate || rowDate <= toDate;

      return matchSearch && matchFrom && matchTo;
    });
  }, [rows, search, fromDate, toDate]);

  const totalAmount = filteredRows.reduce(
    (sum, item) => sum + Number(item.cash_amount || 0),
    0
  );

  const avgAmount =
    filteredRows.length === 0 ? 0 : totalAmount / filteredRows.length;

  const verifiedToday = filteredRows.filter(
    (x) =>
      x.verified_at?.split("T")[0] ===
      new Date().toISOString().split("T")[0]
  ).length;

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: theme.background, transition: 'background 0.3s ease' }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
          ✅ Verified Cash Deposits
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textMuted }}>
          Review all verified cash deposit transactions
        </p>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard
          icon={<WalletIcon />}
          title="Total Verified"
          value={`₹ ${totalAmount.toFixed(2)}`}
          color={theme.success}
          theme={theme}
        />
        <StatCard
          icon={<CheckCircleIcon />}
          title="Transactions"
          value={filteredRows.length}
          color={theme.primary}
          theme={theme}
        />
        <StatCard
          icon={<TrendingIcon />}
          title="Average Deposit"
          value={`₹ ${avgAmount.toFixed(2)}`}
          color={theme.warning}
          theme={theme}
        />
        <StatCard
          icon={<ClockIcon />}
          title="Verified Today"
          value={verifiedToday}
          color="#8B5CF6"
          theme={theme}
        />
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
          alignItems: 'center',
        }}
      >
        {/* Search Input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 10,
            flex: 1,
            minWidth: 240,
            maxWidth: 400,
          }}
        >
          <SearchIcon />
          <input
            placeholder="Search Driver / Restaurant / Order"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              padding: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: theme.textPrimary,
              fontSize: 13,
            }}
          />
        </div>

        {/* From Date */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 10,
          }}
        >
          <CalendarIcon />
          <span style={{ fontSize: 12, color: theme.textMuted }}>From:</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              padding: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: theme.textPrimary,
              fontSize: 13,
              cursor: 'pointer',
            }}
          />
        </div>

        {/* To Date */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 10,
          }}
        >
          <CalendarIcon />
          <span style={{ fontSize: 12, color: theme.textMuted }}>To:</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              padding: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: theme.textPrimary,
              fontSize: 13,
              cursor: 'pointer',
            }}
          />
        </div>

        {/* Clear Filters */}
        {(search || fromDate || toDate) && (
          <button
            onClick={() => {
              setSearch('');
              setFromDate('');
              setToDate('');
            }}
            style={{
              padding: '10px 16px',
              background: `${theme.error || theme.danger}1a`,
              color: theme.error || theme.danger,
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div
          style={{
            textAlign: 'center',
            padding: 60,
            background: theme.surface,
            borderRadius: 16,
            border: `1px solid ${theme.border}`,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${theme.border}`,
              borderTopColor: theme.primary,
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: theme.textSecondary, fontSize: 14 }}>
            Loading verified deposits...
          </p>
        </div>
      ) : (
        <div
          style={{
            background: theme.surface,
            borderRadius: 16,
            overflow: 'hidden',
            border: `1px solid ${theme.border}`,
            boxShadow: `0 4px 12px ${theme.primary}08`,
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 900,
              }}
            >
              <thead>
                <tr style={{ background: theme.surfaceLighter }}>
                  <th style={thStyle(theme)}>#</th>
                  <th style={thStyle(theme)}>Driver</th>
                  <th style={thStyle(theme)}>Restaurant</th>
                  <th style={thStyle(theme)}>Order</th>
                  <th style={thStyle(theme)}>Amount</th>
                  <th style={thStyle(theme)}>Collected By</th>
                  <th style={thStyle(theme)}>Verified By</th>
                  <th style={thStyle(theme)}>Verified At</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((item, index) => (
                    <tr
                      key={item.collection_id}
                      style={{
                        borderBottom: `1px solid ${theme.border}`,
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          theme.surfaceHover || `${theme.primary}08`)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'transparent')
                      }
                    >
                      <td style={tdStyle(theme, theme.textSecondary)}>
                        {index + 1}
                      </td>
                      <td style={tdStyle(theme, theme.textPrimary, true)}>
                        {item.driver_name || '-'}
                      </td>
                      <td style={tdStyle(theme, theme.textSecondary)}>
                        {(item.orders || []).map((o, i) => (
                          <div key={o.id}>
                            {o.restaurant_name}
                            {i < item.orders.length - 1 && (
                              <span style={{ color: theme.textMuted, fontSize: 10 }}> • </span>
                            )}
                          </div>
                        ))}
                      </td>
                      <td style={tdStyle(theme, theme.primary)}>
                        {(item.orders || []).map((o) => (
                          <div
                            key={o.id}
                            style={{ fontFamily: 'monospace', fontSize: 12 }}
                          >
                            #{o.id}
                          </div>
                        ))}
                      </td>
                      <td style={{ ...tdStyle(theme, theme.success), fontWeight: 700, fontSize: 14 }}>
                        ₹ {Number(item.cash_amount || 0).toLocaleString()}
                      </td>
                      <td style={tdStyle(theme, theme.textSecondary)}>
                        {item.collected_by_name || "-"}
                      </td>
                      <td style={tdStyle(theme, theme.textPrimary, true)}>
                        {item.verified_by_name || '-'}
                      </td>
                      <td style={{ ...tdStyle(theme, theme.textMuted), fontSize: 12 }}>
                        {new Date(item.verified_at).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      style={{
                        textAlign: "center",
                        padding: 60,
                      }}
                    >
                      <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
                      <h3
                        style={{
                          color: theme.textPrimary,
                          marginBottom: 8,
                          fontSize: 16,
                          fontWeight: 600,
                        }}
                      >
                        No Verified Deposits Found
                      </h3>
                      <p style={{ color: theme.textMuted, fontSize: 13, margin: 0 }}>
                        {search || fromDate || toDate
                          ? "Try adjusting your filters"
                          : "No verified cash deposits available"}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredRows.length > 0 && (
            <div
              style={{
                padding: '14px 20px',
                borderTop: `1px solid ${theme.border}`,
                background: theme.surfaceLighter,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <span style={{ fontSize: 12, color: theme.textSecondary }}>
                Showing <strong style={{ color: theme.textPrimary }}>{filteredRows.length}</strong> verified deposits
              </span>
              <span style={{ fontSize: 12, color: theme.success, fontWeight: 600 }}>
                Total: ₹ {totalAmount.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===================== TABLE STYLES =====================
const thStyle = (theme) => ({
  padding: "14px 16px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: theme.primary,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${theme.border}`,
});

const tdStyle = (theme, color, isBold = false) => ({
  padding: "14px 16px",
  fontSize: 13,
  color: color || theme.textPrimary,
  fontWeight: isBold ? 600 : 400,
  whiteSpace: "nowrap",
});